import { useState, ImgHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import {
  cdnAvifSet,
  cdnAvifSrcSet,
  cdnImage,
  cdnSrcSet,
  cdnWidthSet,
  shouldProxy,
} from "@/lib/images";

export interface SmartImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> {
  src?: string | null;
  /** Rendered width in CSS px, drives the proxy resize. */
  w?: number;
  /** Rendered height in CSS px. */
  h?: number;
  quality?: number;
  fit?: "cover" | "contain" | "inside" | "outside" | "fill";
  /** Above-the-fold image: eager + high fetch priority. */
  priority?: boolean;
  /** Shown when the source is missing or fails entirely. */
  fallbackSrc?: string;
  /** Neutral background while loading. */
  showPlaceholder?: boolean;
  /** Layout hint, e.g. "(min-width: 768px) 50vw, 100vw". Enables width-based srcset. */
  sizes?: string;
  /** Candidate widths used with `sizes`. */
  widths?: number[];
  /**
   * Skip the publisher original if our stored copy is missing. Used for small
   * thumbnails, where the original can be a multi-hundred-KB full-size photo.
   */
  noOriginalFallback?: boolean;
}

/**
 * <img> wrapper that serves resized WebP through the image proxy, reserves
 * layout space, lazy-loads by default, and falls back to the original URL (then
 * a placeholder) if the proxy or the source fails.
 */
const SmartImage = ({
  src,
  w,
  h,
  quality,
  fit = "cover",
  priority = false,
  fallbackSrc = "/placeholder.svg",
  showPlaceholder = true,
  sizes,
  widths,
  noOriginalFallback = false,
  className,
  alt = "",
  onLoad,
  onError,
  ...rest
}: SmartImageProps) => {
  // 0 = proxied, 1 = original url, 2 = placeholder
  const [stage, setStage] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const original = src || "";
  const canProxy = shouldProxy(original);

  let resolvedSrc: string;
  let srcSet: string | undefined;
  let avifSet: string | undefined;

  if (!original) {
    resolvedSrc = fallbackSrc;
  } else if (stage === 0 && canProxy) {
    resolvedSrc = cdnImage(original, { width: w, height: h, quality, fit });
    srcSet = sizes
      ? cdnWidthSet(original, widths, { width: w, height: h, quality, fit })
      : cdnSrcSet(original, { width: w, height: h, quality, fit });
    avifSet = sizes
      ? cdnAvifSet(original, widths, { width: w, height: h, quality, fit })
      : cdnAvifSrcSet(original, { width: w, height: h, quality, fit });
  } else if (stage <= 1) {
    resolvedSrc = original;
  } else {
    resolvedSrc = fallbackSrc;
  }

  const image = (
    <img
      {...rest}
      src={resolvedSrc}
      srcSet={srcSet}
      sizes={srcSet && sizes ? sizes : undefined}
      alt={alt}
      width={rest.width ?? w}
      height={rest.height ?? h}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "sync" : "async"}
      fetchPriority={priority ? "high" : undefined}
      referrerPolicy="no-referrer"
      className={cn(
        showPlaceholder && !loaded && "bg-muted animate-pulse",
        "transition-opacity duration-200",
        className
      )}
      onLoad={(e) => {
        setLoaded(true);
        onLoad?.(e);
      }}
      onError={(e) => {
        if (stage < 2) {
          // Small thumbnails skip the publisher original: it can be a
          // full-size photo many times bigger than the box it fills.
          setStage(noOriginalFallback ? 2 : (s) => s + 1);
        } else {
          onError?.(e);
        }
      }}
    />
  );

  if (!avifSet) return image;

  // display:contents keeps the wrapper out of the layout entirely.
  return (
    <picture style={{ display: "contents" }}>
      <source type="image/avif" srcSet={avifSet} sizes={sizes} />
      {image}
    </picture>
  );
};

export default SmartImage;
